import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogActions,
} from "@mui/material";

const PremiumListing = ({
  listingData,
  accessToken,
  navigate,
  cancelDialogOpen,
  setCancelDialogOpen,
  handleCancelPremium,
  handleExtendPremium,
  handleReactivatePremium,
  updatePremiumOptIn,
}) => {
  // Calculate trial days remaining (rounded up)
  const getTrialDaysRemaining = () => {
    if (!listingData.trial_ends_at) return 0;

    const now = new Date();
    const trialEnd = new Date(listingData.trial_ends_at);
    const diffMs = trialEnd - now;

    if (diffMs <= 0) return 0;

    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  const daysSincePremiumStart = () => {
    if (!listingData.premium_started_at) return 0;
    const now = new Date();
    const premiumStart = new Date(listingData.premium_started_at);
    const diffMs = now - premiumStart;
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  };

  const showExtendButton = daysSincePremiumStart() >= 14;

  if (!listingData) return null;

  // Correctly calculate if the trial is over
  const trialOver = listingData.is_trial
    ? listingData.trial_ends_at
      ? new Date() >= new Date(listingData.trial_ends_at)
      : false // trial ongoing if no end date set (or treat as not over)
    : true; // no trial at all, so trialOver = false

  const isCanceled = listingData.is_scheduled_to_cancel;

  // Show upgrade prompt if not premium but has landlord or pro id
  if (
    (listingData.is_premium === false || listingData.is_premium === null) &&
    (listingData.landlord_id !== null || listingData.pro_id !== null)
  ) {
    return (
      <Paper
        elevation={3}
        sx={{ padding: 4, maxWidth: "800px", margin: "auto", marginTop: 4 }}
      >
        <Typography variant="h5" gutterBottom>
          Upgrade to Premium Service
        </Typography>
        <Typography sx={{ marginBottom: 2 }}>
          Make your listing stand out with the following benefits:
        </Typography>
        <ul>
          <li>Feature at the top of search results</li>
          <li>Highlighted listing with a premium badge</li>
          <li>Promoted on the homepage banner</li>
          <li>Advanced analytics and performance insights</li>
        </ul>
        <Box display="flex" justifyContent="space-between" marginTop={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={updatePremiumOptIn}
          >
            Confirm Premium Upgrade
          </Button>
        </Box>
      </Paper>
    );
  }

  // Premium active UI
  if (listingData.is_premium === true) {
    const premiumStart = new Date(listingData.premium_started_at);
    const trialEnd = listingData.trial_ends_at
      ? new Date(listingData.trial_ends_at)
      : new Date(premiumStart.getTime() + 7 * 24 * 60 * 60 * 1000);

    return (
      <>
        <Paper
          elevation={3}
          sx={{ padding: 4, maxWidth: "800px", margin: "auto", marginTop: 4 }}
        >
          <Typography variant="h5" gutterBottom>
            Current Premium Service
          </Typography>
          <Typography sx={{ marginBottom: 2 }}>
            Your listing currently has a premium subscription with the following
            details:
          </Typography>
          <Typography>
            <strong>Start Date:</strong> {premiumStart.toLocaleString()}
          </Typography>
          {listingData.trial_ends_at && (
            <Typography>
              <strong>Trial Ends:</strong> {trialEnd.toLocaleString()}
            </Typography>
          )}
          <Typography>
            <strong>Service Ends:</strong>{" "}
            {new Date(listingData.premium_expires_at).toLocaleString()}
          </Typography>

          {/* Trial canceled but still active */}
          {listingData.is_trial && isCanceled && (
            <>
              <Typography color="warning.main" sx={{ mt: 1 }}>
                You have canceled your free trial. Your listing will remain
                premium until the trial period ends. After that, this listing
                will no longer be eligible for another free trial.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleReactivatePremium}
                >
                  Reactivate Premium
                </Button>
              </Box>
            </>
          )}

          {/* Trial ended but premium active */}
          {!listingData.is_trial && trialOver && listingData.trial_ends_at && (
            <Typography color="info.main" sx={{ mt: 1 }}>
              Your free trial has ended. Your premium service is active and you
              can use all premium features until the service end date. After
              that, your listing will be shown as a normal listing.
            </Typography>
          )}

          {!listingData.is_trial && trialOver && !listingData.trial_ends_at && (
            <Typography color="info.main" sx={{ mt: 1 }}>
              Your premium service is active and you can use all premium
              features until the service end date. After that, your listing will
              be shown as a normal listing.
            </Typography>
          )}

          {/* Trial active and NOT canceled */}
          {listingData.is_trial &&
            listingData.trial_ends_at &&
            getTrialDaysRemaining() > 0 &&
            !isCanceled && (
              <>
                <Typography color="warning.main" sx={{ mt: 1 }}>
                  You are currently on a{" "}
                  <strong>{getTrialDaysRemaining()}-day free trial</strong>.
                  Your premium access will automatically renew unless canceled
                  before the trial ends.
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    Cancel Premium Service
                  </Button>
                </Box>
              </>
            )}

          {/* Premium scheduled to cancel but not trial */}
          {isCanceled && !listingData.is_trial && (
            <Box sx={{ mt: 2 }}>
              <Typography color="warning.main" sx={{ mt: 1 }}>
                Your premium service is scheduled to cancel at the end of the
                current billing cycle.
              </Typography>
            </Box>
          )}

          {/* Show extend premium button only if trial is over and premium NOT canceled */}
          {showExtendButton && (
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ mt: 1 }}>
                Your listing will no longer appear as premium after the end date
                unless reactivated.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleExtendPremium}
              >
                Extend Premium Service by 1 Month
              </Button>
            </Box>
          )}
        </Paper>

        {/* Cancel Dialog */}
        <Dialog
          open={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
        >
          <DialogTitle>Cancel Premium Service</DialogTitle>
          <DialogContentText sx={{ padding: "0 24px" }}>
            Are you sure you want to cancel your premium subscription?{" "}
            {listingData.is_trial && (
              <>
                You will still be able to use all premium services until the end
                of your current trial period. Once your trial ends, you won’t be
                able to start another free trial for this listing.
              </>
            )}
          </DialogContentText>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)} color="primary">
              No
            </Button>
            <Button onClick={handleCancelPremium} color="error" autoFocus>
              Yes, Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return null;
};

export default PremiumListing;
